import { useState, type ReactNode } from 'react';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { useNavigate } from '@tanstack/react-router';
import {
  Sparkles,
  Layers,
  Clapperboard,
  ChevronRight,
  Trash2,
  RotateCcw,
  AlertTriangle,
  Loader2,
  Inbox,
  FileUp,
  FileText,
  Radio,
} from 'lucide-react';
import { api } from '@/lib/api';
import { Button } from '@/components/ui/button';
import { Modal } from '@/components/ui/modal';
import { toast } from '@/components/ui/toast';
import { AgentWorkflowDialog } from '@/pages/writer/AgentWorkflowDialog';
import { DirectorWorkflowDialog } from '@/pages/director/DirectorWorkflowDialog';
import { DocumentImportDialog } from '@/pages/radar/DocumentImportDialog';

interface ProjectListItem {
  episodeId: string;
  ipName: string | null;
  topicTitle: string | null;
  audienceLocked: boolean;
  status: 'draft' | 'initialized';
  ownerName: string | null;
  initializedAt: string | null;
  updatedAt: string | null;
  genreTags: string[];
  episodeCount: number | null;
  totalEpisodes: number | null;
  agentCompletedAt: string | null;
  writerCompletedAt: string | null;
  directorInitializedAt: string | null;
  directorInitializingAt: string | null;
  deletedAt: string | null;
  sourceKind: 'hotspot' | 'document';
  sourceFilename: string | null;
}

interface ProjectListResponse {
  items: ProjectListItem[];
  scope: 'self' | 'tenant' | 'all';
  role: string;
  count: number;
}

type Action = 'delete' | 'restore' | 'permanent';

const GRADIENTS = [
  'from-rose-500 to-orange-500',
  'from-violet-500 to-fuchsia-500',
  'from-sky-500 to-cyan-500',
  'from-emerald-500 to-teal-500',
  'from-amber-500 to-yellow-400',
  'from-indigo-500 to-blue-500',
  'from-pink-500 to-purple-500',
];

function pickGradient(seed: string): string {
  let h = 0;
  for (let i = 0; i < seed.length; i++) h = (h * 31 + seed.charCodeAt(i)) | 0;
  return GRADIENTS[Math.abs(h) % GRADIENTS.length];
}

function fmtTime(s: string | null): string {
  if (!s) return '';
  const d = new Date(s);
  if (Number.isNaN(d.getTime())) return '';
  const diff = Math.max(0, Date.now() - d.getTime());
  const min = Math.floor(diff / 60_000);
  if (min < 1) return '刚刚';
  if (min < 60) return `${min} 分钟前`;
  const hr = Math.floor(min / 60);
  if (hr < 24) return `${hr} 小时前`;
  const day = Math.floor(hr / 24);
  if (day < 30) return `${day} 天前`;
  return `${d.getMonth() + 1}/${d.getDate()}`;
}

export function ProjectsHall({
  hideHeader = false,
  trashed = false,
  titleNode,
}: { hideHeader?: boolean; trashed?: boolean; titleNode?: ReactNode } = {}) {
  const navigate = useNavigate();
  const qc = useQueryClient();
  const queryKey = trashed ? ['writer', 'trash'] : ['writer', 'projects'];
  const endpoint = trashed ? 'writer/episodes/trash' : 'writer/episodes';
  const { data, isLoading, error } = useQuery({
    queryKey,
    queryFn: () => api.get(endpoint).json<ProjectListResponse>(),
    refetchInterval: trashed ? false : 30_000,
  });

  const items = data?.items ?? [];

  const [confirm, setConfirm] = useState<{
    ep: string;
    title: string;
    action: Action;
  } | null>(null);
  const [agentTarget, setAgentTarget] = useState<string | null>(null);
  const [directorTarget, setDirectorTarget] = useState<string | null>(null);
  const [importOpen, setImportOpen] = useState(false);

  const mut = useMutation({
    mutationFn: async ({ ep, action }: { ep: string; action: Action }) => {
      if (action === 'delete')
        return api.delete(`writer/episodes/${ep}`).json<{ episodeId: string }>();
      if (action === 'restore')
        return api.post(`writer/episodes/${ep}/restore`).json<{ episodeId: string }>();
      return api
        .delete(`writer/episodes/${ep}/permanent`)
        .json<{ episodeId: string }>();
    },
    onSuccess: (_data, vars) => {
      qc.invalidateQueries({ queryKey: ['writer', 'projects'] });
      qc.invalidateQueries({ queryKey: ['writer', 'trash'] });
      const tip =
        vars.action === 'delete'
          ? '已移入回收站'
          : vars.action === 'restore'
          ? '已恢复'
          : '已永久删除';
      toast.success(tip);
      setConfirm(null);
    },
    onError: (err: Error) => toast.error('操作失败', err.message || '稍后重试'),
  });

  const handleCardClick = (p: ProjectListItem) => {
    if (trashed) return;
    if (!p.agentCompletedAt) {
      setAgentTarget(p.episodeId);
      return;
    }
    if (!p.writerCompletedAt) {
      navigate({
        to: '/writer/$episodeId' as never,
        params: { episodeId: p.episodeId } as never,
      });
      return;
    }
    if (!p.directorInitializedAt) {
      setDirectorTarget(p.episodeId);
      return;
    }
    navigate({
      to: '/director/$episodeId' as never,
      params: { episodeId: p.episodeId } as never,
    });
  };

  const titleOf = (p: ProjectListItem) => p.ipName || p.topicTitle || p.episodeId;

  const headerLabel = trashed ? '回收站' : '项目大厅';
  const headerSub = trashed
    ? '已删除项目 · 可恢复 / 永久删除'
    : '已立项项目 · 点击卡片直达脚本微调';

  return (
    <section className="space-y-3 pt-4">
      {!hideHeader && (
        <header className="flex items-end justify-between gap-3">
          <div>
            <div className="flex items-center gap-2 text-sm text-neutral-500">
              {trashed ? (
                <Trash2 className="h-4 w-4 text-danger" />
              ) : (
                <Layers className="h-4 w-4 text-brand-500" />
              )}
              <span>{headerSub}</span>
            </div>
            <h2 className="mt-0.5 text-xl font-semibold tracking-tight">{headerLabel}</h2>
          </div>
          <div className="flex items-center gap-3">
            {data && data.count > 0 && (
              <span className="text-xs text-neutral-500">
                共 {data.count} 个 ·{' '}
                {data.scope === 'self' ? '我的' : data.scope === 'tenant' ? '租户内' : '全部'}
              </span>
            )}
            {!trashed && (
              <Button
                variant="agent"
                agentColor="writer"
                size="sm"
                onClick={() => setImportOpen(true)}
              >
                <FileUp className="h-4 w-4" /> 导入文档立项
              </Button>
            )}
          </div>
        </header>
      )}
      {hideHeader && (
        <div className="flex flex-wrap items-end justify-between gap-3">
          {titleNode ? <div className="min-w-0 flex-1">{titleNode}</div> : <span />}
          <div className="flex items-center gap-3">
            {data && data.count > 0 && (
              <span className="text-xs text-neutral-500">
                共 {data.count} 个 ·{' '}
                {data.scope === 'self' ? '我的' : data.scope === 'tenant' ? '租户内' : '全部'}
              </span>
            )}
            {!trashed && (
              <Button
                variant="agent"
                agentColor="writer"
                size="sm"
                onClick={() => setImportOpen(true)}
              >
                <FileUp className="h-4 w-4" /> 导入文档立项
              </Button>
            )}
          </div>
        </div>
      )}

      {isLoading ? (
        <div className="grid grid-cols-1 gap-3 sm:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4">
          {Array.from({ length: 4 }).map((_, i) => (
            <div key={i} className="h-44 animate-pulse rounded-xl bg-surface-variant" />
          ))}
        </div>
      ) : error ? (
        <div className="rounded-lg border border-danger/30 bg-danger-soft/30 p-3 text-xs text-danger">
          加载失败,稍后重试
        </div>
      ) : items.length === 0 ? (
        <div className="flex flex-col items-center gap-2 rounded-lg border border-dashed border-outline-variant/50 p-12 text-center text-sm text-neutral-500">
          {trashed ? (
            <>
              <Inbox className="h-6 w-6 text-outline" />
              <p>回收站为空 · 删除的项目会在这里展示</p>
            </>
          ) : (
            <p>还没有已立项项目 · 在热点雷达挑一条热点,启动 IP 化 → 立项 → 编剧 Agent</p>
          )}
        </div>
      ) : (
        <div className="grid grid-cols-1 gap-3 sm:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4">
          {items.map((p) => {
            const title = titleOf(p);
            const grad = pickGradient(title);
            const tags = (p.genreTags || []).slice(0, 3);
            const total = p.totalEpisodes ?? 0;
            const target = p.episodeCount ?? 0;
            const completed = target > 0 && total > 0;
            const agentDone = Boolean(p.agentCompletedAt);
            const lastTime = trashed
              ? p.deletedAt
              : p.updatedAt || p.agentCompletedAt || p.initializedAt;
            return (
              <div
                key={p.episodeId}
                className={
                  trashed
                    ? 'group relative overflow-hidden rounded-xl border border-outline-variant/40 bg-surface text-left opacity-90 shadow-sm transition'
                    : 'group relative overflow-hidden rounded-xl border border-outline-variant/40 bg-surface text-left shadow-sm transition hover:border-brand-300 hover:shadow-md'
                }
              >
                {!trashed && (
                  <button
                    type="button"
                    onClick={(e) => {
                      e.stopPropagation();
                      setConfirm({ ep: p.episodeId, title, action: 'delete' });
                    }}
                    className="absolute bottom-2 right-2 z-10 flex h-7 w-7 items-center justify-center rounded-full bg-surface/95 text-neutral-400 opacity-0 shadow-sm ring-1 ring-outline-variant/40 transition hover:bg-danger/10 hover:text-danger group-hover:opacity-100"
                    title="移入回收站"
                  >
                    <Trash2 className="h-3.5 w-3.5" />
                  </button>
                )}
                <button
                  type="button"
                  onClick={() => handleCardClick(p)}
                  disabled={trashed}
                  className="block w-full text-left"
                >
                  <div className={`h-2 w-full bg-gradient-to-r ${grad}`} />
                  <div className="space-y-2 p-3.5">
                    <div className="flex items-start justify-between gap-2">
                      <h3
                        className={
                          trashed
                            ? 'line-clamp-2 flex-1 text-sm font-semibold leading-snug text-neutral-700 line-through decoration-neutral-300'
                            : 'line-clamp-2 flex-1 text-sm font-semibold leading-snug text-neutral-900 group-hover:text-brand-600'
                        }
                      >
                        {title}
                      </h3>
                      {!trashed &&
                        (agentDone ? (
                          <ChevronRight className="mt-0.5 h-4 w-4 shrink-0 text-neutral-300 transition group-hover:translate-x-0.5 group-hover:text-brand-500" />
                        ) : (
                          <Loader2 className="mt-0.5 h-4 w-4 shrink-0 animate-spin text-brand-500" />
                        ))}
                    </div>
                    {p.sourceKind === 'document' && p.sourceFilename ? (
                      <div className="flex items-center gap-1 text-[11px] leading-none text-violet-600">
                        <FileText className="h-3 w-3 shrink-0" />
                        <span className="truncate">
                          来自文档:{p.sourceFilename}
                        </span>
                      </div>
                    ) : p.topicTitle && p.topicTitle !== title ? (
                      <div className="flex items-center gap-1 text-[11px] leading-none text-amber-600">
                        <Radio className="h-3 w-3 shrink-0" />
                        <span className="truncate">
                          来自热点:{p.topicTitle}
                        </span>
                      </div>
                    ) : null}
                    {tags.length > 0 && (
                      <div className="flex flex-wrap gap-1">
                        {tags.map((t) => (
                          <span
                            key={t}
                            className="rounded-full bg-brand-50 px-1.5 py-0.5 text-[10px] text-brand-700"
                          >
                            {t}
                          </span>
                        ))}
                      </div>
                    )}
                    <div className="flex flex-wrap items-center gap-2 text-[11px] text-neutral-500">
                      {target > 0 && (
                        <span className="inline-flex items-center gap-0.5">
                          <Clapperboard className="h-3 w-3" /> {target} 集
                        </span>
                      )}
                      {!trashed && p.directorInitializedAt ? (
                        <span className="inline-flex items-center gap-0.5 text-success">
                          <Clapperboard className="h-3 w-3" /> 可进入制作
                        </span>
                      ) : !trashed && p.writerCompletedAt ? (
                        <span className="inline-flex items-center gap-0.5 text-warning">
                          <Loader2 className="h-3 w-3 animate-spin" /> 制作台初始化中
                        </span>
                      ) : (
                        <>
                          {!trashed && completed && (
                            <span
                              className={
                                agentDone
                                  ? 'inline-flex items-center gap-0.5 text-success'
                                  : 'inline-flex items-center gap-0.5 text-warning'
                              }
                            >
                              <Sparkles className="h-3 w-3" />
                              {agentDone ? '已生成' : `生成中 ${total}/${target}`}
                            </span>
                          )}
                          {!trashed && !agentDone && (
                            <span className="inline-flex items-center gap-0.5 text-warning">
                              <Loader2 className="h-3 w-3 animate-spin" /> Agent 进行中
                            </span>
                          )}
                        </>
                      )}
                      {lastTime && (
                        <span className="ml-auto">
                          {trashed ? '删除于 ' : ''}
                          {fmtTime(lastTime)}
                        </span>
                      )}
                    </div>
                  </div>
                </button>
                {trashed && (
                  <div className="flex gap-2 border-t border-outline-variant/30 bg-surface-variant/30 px-3 py-2">
                    <Button
                      variant="secondary"
                      size="sm"
                      onClick={() =>
                        setConfirm({ ep: p.episodeId, title, action: 'restore' })
                      }
                      disabled={mut.isPending}
                      className="flex-1"
                    >
                      <RotateCcw className="h-3.5 w-3.5" /> 恢复
                    </Button>
                    <Button
                      variant="danger"
                      size="sm"
                      onClick={() =>
                        setConfirm({ ep: p.episodeId, title, action: 'permanent' })
                      }
                      disabled={mut.isPending}
                      className="flex-1"
                    >
                      <Trash2 className="h-3.5 w-3.5" /> 永久删除
                    </Button>
                  </div>
                )}
              </div>
            );
          })}
        </div>
      )}

      <ConfirmModal
        target={confirm}
        onCancel={() => setConfirm(null)}
        onOk={() =>
          confirm && mut.mutate({ ep: confirm.ep, action: confirm.action })
        }
        submitting={mut.isPending}
      />

      <AgentWorkflowDialog
        open={Boolean(agentTarget)}
        onOpenChange={(o) => {
          if (!o) setAgentTarget(null);
        }}
        episodeId={agentTarget ?? ''}
        onFinish={() => {
          qc.invalidateQueries({ queryKey: ['writer', 'projects'] });
        }}
        onSwitchToRefine={() => {
          const ep = agentTarget;
          setAgentTarget(null);
          if (ep)
            navigate({
              to: '/writer/$episodeId' as never,
              params: { episodeId: ep } as never,
            });
        }}
      />

      <DirectorWorkflowDialog
        open={Boolean(directorTarget)}
        onOpenChange={(o) => {
          if (!o) setDirectorTarget(null);
        }}
        episodeId={directorTarget ?? ''}
        onFinish={() => {
          qc.invalidateQueries({ queryKey: ['writer', 'projects'] });
        }}
        onEnterDirector={() => {
          const ep = directorTarget;
          setDirectorTarget(null);
          if (ep)
            navigate({
              to: '/director/$episodeId' as never,
              params: { episodeId: ep } as never,
            });
        }}
      />

      <DocumentImportDialog
        open={importOpen}
        onOpenChange={setImportOpen}
      />
    </section>
  );
}

function ConfirmModal({
  target,
  onCancel,
  onOk,
  submitting,
}: {
  target: { ep: string; title: string; action: Action } | null;
  onCancel: () => void;
  onOk: () => void;
  submitting: boolean;
}) {
  if (!target) return null;
  const isPermanent = target.action === 'permanent';
  const isRestore = target.action === 'restore';
  const title = isPermanent
    ? '永久删除项目'
    : isRestore
    ? '恢复项目'
    : '删除项目';
  const description = isPermanent
    ? '此操作不可撤销:分集脚本、Story Atlas 全部数据将永久移除。'
    : isRestore
    ? '恢复后可在项目大厅继续工作。'
    : '项目将移入回收站,可在 30 天内恢复。';
  return (
    <Modal
      open={Boolean(target)}
      onOpenChange={(o) => {
        if (!o) onCancel();
      }}
      title={title}
      description={description}
      className="max-w-sm"
      footer={
        <>
          <Button variant="ghost" size="sm" onClick={onCancel} disabled={submitting}>
            取消
          </Button>
          <Button
            variant={isPermanent ? 'danger' : isRestore ? 'agent' : 'danger'}
            agentColor={isRestore ? 'writer' : undefined}
            size="sm"
            onClick={onOk}
            disabled={submitting}
          >
            {submitting
              ? '处理中…'
              : isPermanent
              ? '永久删除'
              : isRestore
              ? '确认恢复'
              : '移入回收站'}
          </Button>
        </>
      }
    >
      <div
        className={
          isPermanent
            ? 'flex items-start gap-2 rounded-lg bg-danger-soft/40 p-3 text-xs leading-relaxed text-danger'
            : 'rounded-lg bg-surface-variant/40 p-3 text-xs leading-relaxed text-neutral-700'
        }
      >
        {isPermanent && <AlertTriangle className="mt-0.5 h-4 w-4 shrink-0" />}
        <div>
          <strong className="font-semibold">「{target.title}」</strong>
        </div>
      </div>
    </Modal>
  );
}
